/* eslint-disable no-use-before-define */
import { UnitSystem } from '@ravago/shared/page-data/models/config';

import { FacetFilter, FilterRange } from './facet-filter.model';
import { PageResponse } from './page-response.model';

export interface SearchQueryArgs {
  searchType: ProductRequestType;
  searchRequest: ProductRequest;
  sort?: Sort[];
  page: number;
  size: number;
  language?: string;
}

export interface SearchQueryArgsV2 {
  searchType: ProductRequestType;
  searchRequest: ProductRequestV2;
  sort?: Sort[];
  page: number;
  size: number;
  language?: string;
  unit: UnitSystem;
}

export type FacetKeysV2 = keyof ElasticFacetsV2;
export type CollectionFacetKeysV2 = keyof CollectionFacetsV2;

export interface CollectionFacetsV2 {
  'technical-properties': (keyof Pick<ElasticFacetsV2, 'flame-rating' | 'density' | 'flow-rate' | 'notched-impact' | 'notched-impact-strength'>)[];
}
export const technicalProperties: CollectionFacetsV2['technical-properties'] = [
  'flame-rating',
  'density',
  'flow-rate',
  'notched-impact',
  'notched-impact-strength'
];
export const collectionFacetKeysV2: CollectionFacetKeysV2[] = ['technical-properties'];
export const collectionGroups: CollectionFacetsV2 = {
  'technical-properties': technicalProperties
};

export interface ElasticFacetsV2 {
  country?: string[];
  brand?: string[];
  producer?: string[];
  group?: string[];
  subgroup?: string[];
  quality?: string[];
  feature?: string[];
  filler?: { key: string; range?: FilterRange }[];
  industry?: string[];
  'processing-method'?: string[];
  segment?: string[];
  'sustainability-group'?: string[];
  'sub-sustainability'?: string[];
  visibility?: string[];
  'flame-rating'?: string[];
  density?: FilterRange;
  'flow-rate'?: FilterRange;
  'notched-impact'?: FilterRange;
  'notched-impact-strength'?: FilterRange;
}

export interface ProductRequest {
  query: string;
  facets: FacetFilter[];
}

export interface ProductRequestV2 extends ElasticFacetsV2 {
  query?: string;
}

export interface ProductsQueryResponse<T> {
  content: T[];
  facets: FacetResponse[];
  totalElements: number;
  totalPages: number;
  documentCounts: ProductDocumentCounts;
}

export interface ProductsQueryV2Response<T> {
  content: T[];
  facets: FacetV2Response;
  totalElements: number;
  totalPages: number;
  documentCounts: ProductDocumentCountsV2;
}

export interface ProductDocumentCounts {
  products: number;
  caseStudies: number;
  brands: number;
}

export interface ProductDocumentCountsV2 {
  product: number;
  caseStudy: number;
  brand: number;
}

export interface ProductRespond {
  searchResult: PageResponse<DocumentResponse>;
  aggregatedFacets: FacetResponse[];
  documentCounts: ProductDocumentCounts;
}

export interface ProductV2Response {
  searchResult: PageResponse<DocumentResponse>;
  filters: FacetV2Response;
  documentCounts: ProductDocumentCountsV2;
}

export type FacetV2Response = {
  [key in FacetKeysV2]?: FacetContentV2Response[];
};

export interface FacetResponse {
  identifier: FacetIdentityResponse;
  facets: FacetContentResponse[];
}

export interface TypedDocumentResponse {
  type: DocumentResponseType;
}

export type DocumentResponse = TypedDocumentResponse & {
  id: number;
  label: string;
} & (
    | BrandFacetResponse
    | CaseStudyResponse
    | FeatureFacetResponse
    | FillerFacetResponse
    | GroupFacetResponse
    | IndustryFacetResponse
    | ProcessingMethodFacetResponse
    | ProducerFacetResponse
    | ProductPortfolioResponse
    | QualityFacetResponse
    | SellingCountryFacetResponse
    | SubGroupFacetResponse
  );

export interface FacetIdentityResponse {
  key: string;
  label: string;
}

export interface FacetContentResponse {
  name: string;
  alias?: string;
  quantity: number;
  range?: FilterRange;
  nestedFacets: FacetResponse[];
}

export interface FacetContentV2Response {
  label: string;
  key: string;
  count: number;
  alias?: string;
  range?: FilterRange;
  nested?: FacetV2Response;
  unit?: 'SI' | 'US';
}

interface BusinessContextResponse {
  id: number;
  visibility: string;
  rank: string;
}

export interface ProcessingMethodResponse {
  name: string;
  slug: string;
}

export interface SustainabilityResponse {
  id: number;
  name: string;
  label: string;
  icon: string;
}

export interface SustainabilityGroupResponse {
  id: number;
  name: string;
  subSustainability: SustainabilityResponse;
}

export interface IndustryResponse {
  id?: number;
  name: string;
  segments: SegmentResponse[];
  slug: string;
  imageUrl?: string;
}
export interface ProductResponse {
  id: number;
  name: string;
  slug: string;
  businessContextId?: number;
}

export interface SegmentResponse {
  id?: number;
  name: string;
  rank?: number;
  businessContextId?: number;
  imageUrl?: string;
}

export interface CaseStudyProductResponse {
  id: number;
  name: string;
  slug: string;
}

export interface PhysicalPropertyResponse {
  testingMethod: string;
  value: string;
  condition?: string;
  unit?: 'US' | 'SI';
}

export interface FillersResponse {
  name: string;
  percentage?: number;
  businessContextId?: number;
}

export interface BrandAggregateResponse {
  name: string;
  businessContextId?: number;
}

export interface SpecificationProviderResponse {
  name: string;
  type: string;
  specifications: SpecificationResponse[];
}

export interface SpecificationResponse {
  name: string;
  businessContextId: number;
}

export interface BrandFacetResponse extends TypedDocumentResponse {
  type: 'Brand';
  brand: string;
  slug: string;
  excerpt: string;
  producer: {
    name: string;
    logoUrl: string;
    logoTitle: string;
  };
  numberOfProducts: number;
  businessContexts: BusinessContextResponse[];
  subGroups: string[];
}

export interface CaseStudyResponse {
  type: 'Case Study';
  slug: string;
  description: string;
  excerpt: string;
  confidential: boolean;
  functionKeyRequirements: string;
  benefits: string;
  link: string;
  caseStudyType: string;
  processingMethods: ProcessingMethodResponse;
  businessContexts: BusinessContextResponse[];
  features: string[];
  industries: IndustryResponse[];
  products: ProductResponse[];
  visibility?: string;
  images?: string[];
}

export interface FeatureFacetResponse {
  type: 'Feature';
  features: string;
}

export interface FillerFacetResponse {
  type: 'Filler';
  fillers: FillersResponse;
}

export interface GroupFacetResponse {
  type: 'Group';
  group: string;
}

export interface IndustryFacetResponse {
  type: 'Industry';
  industries: IndustryResponse;
}

export interface ProcessingMethodFacetResponse {
  type: 'Processing Method';
  processingMethods: ProcessingMethodResponse;
}

export interface ProducerFacetResponse {
  type: 'Producer';
  producer: string;
}

export interface ProductPortfolioResponse {
  type: 'Product';
  slug: string;
  businessContexts: BusinessContextResponse[];
  sellingCountries: string[];
  description: string;
  group: string;
  subgroup: string;
  features: string[];
  fillers: FillersResponse[];
  processingMethods: ProcessingMethodResponse[];
  brand: string;
  brandAggregate: BrandAggregateResponse[];
  producer: string;
  quality: string;
  industries: IndustryResponse[];
  specificationProviders: SpecificationProviderResponse[];
  preferred: number;
  sustainabilityGroup: SustainabilityGroupResponse;
  imageUrl?: string;
  density?: PhysicalPropertyResponse[];
  flowRate?: PhysicalPropertyResponse[];
  flameRating?: PhysicalPropertyResponse[];
  notchedImpact?: PhysicalPropertyResponse[];
  notchedImpactStrength?: PhysicalPropertyResponse[];
}

export interface QualityFacetResponse {
  type: 'Quality';
  quality: string;
}

export interface SellingCountryFacetResponse {
  type: 'Selling Country';
  sellingCountries: SellingCountryFacetResponse;
}

export interface SubGroupFacetResponse {
  type: 'Subgroup';
  subgroup: string;
}

export type ProductRequestType = (typeof ProductRequestTypeKeys)[number];
export type ViewType = (typeof ViewTypeKeys)[number];
export type DocumentResponseType = (typeof DocumentResponseTypeKeys)[number];

export const viewMap: { [view: string]: number } = {
  list: 10,
  grid: 18
};

export const DocumentResponseTypeKeys = [
  'Brand',
  'Case Study',
  'Feature',
  'Filler',
  'Group',
  'Industry',
  'Processing Method',
  'Producer',
  'Product',
  'Quality',
  'Selling Country',
  'Subgroup'
] as const;

export const ProductRequestTypeKeys = ['product', 'caseStudy', 'brand'] as const;

export const ViewTypeKeys = ['list', 'grid'] as const;

export type SortDirection = 'asc' | 'desc';

export interface Sort {
  field: string;
  direction: SortDirection;
  ignoreCase: boolean;
}
